import { useEffect } from 'react';
import { createSiteNavigationElementSchema } from '@/utils/schemaGenerator';
import { initAnalytics } from '@/utils/analytics';

/**
 * SEOHead Component
 * Manages document title, canonical link, meta tags, Open Graph, Twitter Cards, Schema.org JSON-LD & Verification Analytics
 */
export default function SEOHead({
  title = "Online Document Printing Services | Print Anytime, Anywhere | Inchpaper",
  description = "Instant online document printing in India. Upload PDF, Word, images, configure print settings, pay online & collect prints from 24x7 self-service Inchpaper kiosks.",
  keywords = "print pdf online, online document printing, online xerox, black and white printing, color printing, photo printing, spiral binding, same day printing",
  canonical,
  ogImage = "https://print.inchpaper.com/favicon-512.png",
  ogType = "website",
  robots = "index, follow",
  schemas = [],
  geoPosition = "28.4595;77.0266",
  geoPlacename = "Gurgaon, Haryana, India",
  geoRegion = "IN-HR"
}) {
  const currentUrl = canonical || (typeof window !== 'undefined' ? window.location.href.split('?')[0] : 'https://print.inchpaper.com');

  useEffect(() => {
    // 0. Initialize GA4, GTM & Microsoft Clarity
    initAnalytics();

    // 1. Title
    document.title = title;

    // Helper: update or create meta tag
    const setMeta = (attrName, attrValue, content) => {
      let element = document.querySelector(`meta[${attrName}="${attrValue}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attrName, attrValue);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Helper: update or create link tag
    const setLink = (rel, href) => {
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        document.head.appendChild(element);
      }
      element.setAttribute('href', href);
    };

    // 2. Standard Meta Tags & Verification Tokens
    setMeta('name', 'description', description);
    setMeta('name', 'keywords', keywords);
    setMeta('name', 'robots', robots);
    setMeta('name', 'google-site-verification', 'google-site-verification-inchpaper-2026-valid');
    setMeta('name', 'msvalidate.01', 'BAEF937ED2E0ABFBF95F53ECB4B9A72A');
    setLink('canonical', currentUrl);

    // Local SEO Geo Meta Tags
    setMeta('name', 'geo.position', geoPosition);
    setMeta('name', 'geo.placename', geoPlacename);
    setMeta('name', 'geo.region', geoRegion);
    setMeta('name', 'ICBM', geoPosition.replace(';', ', '));

    // 3. Open Graph Tags
    setMeta('property', 'og:title', title);
    setMeta('property', 'og:description', description);
    setMeta('property', 'og:url', currentUrl);
    setMeta('property', 'og:type', ogType);
    setMeta('property', 'og:image', ogImage);
    setMeta('property', 'og:site_name', 'Inchpaper Print');

    // 4. Twitter Cards
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', title);
    setMeta('name', 'twitter:description', description);
    setMeta('name', 'twitter:image', ogImage);

    // 5. Schema.org JSON-LD Scripts
    const existingSchemaScripts = document.querySelectorAll('script[data-seo-schema="true"]');
    existingSchemaScripts.forEach(el => el.remove());

    const allSchemas = Array.isArray(schemas) ? schemas : [schemas];

    // Always append Base Organization, WebSite & SiteNavigationElement Schema
    const baseSchemas = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "Inchpaper Private Limited",
        "url": "https://print.inchpaper.com",
        "logo": "https://print.inchpaper.com/inchpaper-logo.png",
        "sameAs": [
          "https://www.facebook.com/inchpaper",
          "https://www.instagram.com/inchpaper",
          "https://www.linkedin.com/company/inchpaper"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+91-7703960982",
          "contactType": "customer service",
          "areaServed": "IN",
          "availableLanguage": ["en", "hi"]
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "Inchpaper Print",
        "url": "https://print.inchpaper.com",
        "potentialAction": {
          "@type": "SearchAction",
          "target": "https://print.inchpaper.com/faq?q={search_term_string}",
          "query-input": "required name=search_term_string"
        }
      },
      createSiteNavigationElementSchema()
    ];

    [...baseSchemas, ...allSchemas].forEach((schemaObj) => {
      if (!schemaObj) return;
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = JSON.stringify(schemaObj);
      document.head.appendChild(script);
    });

  }, [title, description, keywords, currentUrl, ogImage, ogType, robots, schemas]);

  return null;
}
